/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/**
 * Pure builders/readers for the pending-contact reverse index used by the
 * two-step opt-in flow.
 *
 * Problem: when an inbound "YES" arrives from a phone number, we must find EVERY
 * user who added that number as a pending emergency contact (many senders can
 * add the same number; the recipient may have no MyAmbits account). The existing
 * `phoneIndex` maps phone→one account-owner uid — wrong direction, single-valued.
 *
 * Index shape (RTDB):
 *   /pendingContactIndex/{e164}/{uid} = { contactId, invitedAt }
 * A fan-in: one e164 key holds many uid children. When A adds B, A's client
 * writes one child; when B confirms/declines, the inbound handler resolves all
 * children under B's e164 and updates each user's contact status, then clears
 * the entries.
 *
 * These builders are pure (no firebase import) so the exact write/clear shapes
 * and the read resolution are unit-testable without a DB. PR 1 ships + tests
 * them; PR 2 wires the write on add; PR 3 wires the read on inbound. Nothing
 * here mutates contact status or the SOS/WMH path.
 *
 * Firebase RTDB keys cannot contain . $ # [ ] / — an E.164 number ("+15551234567")
 * is safe (only a leading + and digits), so we use it verbatim as the key. Phone
 * MUST be canonical E.164 on BOTH the write (add) and read (inbound `From`) sides
 * or a confirmation will silently never match — normalize with the same helper
 * (normalizePhone, src/lib/phone.ts) at both call sites.
 */

export interface PendingIndexEntry {
  contactId: string;
  invitedAt: number;
}

/** One resolved pending record: which user added this number, and which of
 *  their contacts it is. */
export interface ResolvedPendingContact {
  uid: string;
  contactId: string;
}

/**
 * Multi-path update to REGISTER a pending contact in the index (called on add,
 * PR 2). Merge into the same atomic update that writes the contact itself.
 */
export function buildPendingIndexWrite(
  e164: string,
  uid: string,
  entry: PendingIndexEntry,
): Record<string, PendingIndexEntry> {
  return { [`pendingContactIndex/${e164}/${uid}`]: entry };
}

/**
 * Multi-path update to CLEAR a user's pending entry for a number (called after
 * the number confirms/declines, or when the contact is removed). Nulls the
 * child; RTDB auto-prunes the {e164} parent once its last child is gone.
 */
export function buildPendingIndexClear(
  e164: string,
  uid: string,
): Record<string, null> {
  return { [`pendingContactIndex/${e164}/${uid}`]: null };
}

/**
 * Resolve the raw RTDB node at `/pendingContactIndex/{e164}` into a flat list of
 * {uid, contactId} — the users the inbound handler must update. Tolerates a
 * null/missing node (no pending records → empty list) and skips malformed
 * children defensively.
 */
export function resolvePendingContacts(node: unknown): ResolvedPendingContact[] {
  if (!node || typeof node !== "object") return [];
  const out: ResolvedPendingContact[] = [];
  for (const [uid, value] of Object.entries(node as Record<string, unknown>)) {
    if (!uid) continue;
    const contactId =
      value && typeof value === "object"
        ? (value as { contactId?: unknown }).contactId
        : undefined;
    if (typeof contactId === "string" && contactId.length > 0) {
      out.push({ uid, contactId });
    }
  }
  return out;
}

/**
 * Pure read-modify-write: apply a new opt-in status to one contact (by id) in a
 * user's emergencyContacts array. Returns a NEW array (or null if the contact
 * isn't found / the input isn't an array, so the caller can skip the write).
 *
 * Used by api/sms-inbound (PR 3) when an inbound YES→confirmed / STOP→declined
 * lands: the webhook loads the array via Admin SDK, applies this, writes back.
 * Kept pure so the confirm/decline mutation is unit-testable without a DB.
 * `confirmedAt` is stamped only on a confirm.
 */
export function applyContactStatus(
  contacts: unknown,
  contactId: string,
  status: "confirmed" | "declined",
  now: number,
): Array<Record<string, unknown>> | null {
  if (!Array.isArray(contacts)) return null;
  let found = false;
  const next = contacts.map((c) => {
    if (c && typeof c === "object" && (c as { id?: unknown }).id === contactId) {
      found = true;
      const updated: Record<string, unknown> = { ...(c as object), status };
      if (status === "confirmed") updated.confirmedAt = now;
      return updated;
    }
    return c as Record<string, unknown>;
  });
  return found ? next : null;
}
